// Command palette — open-state + global keybind + result wiring hook.
//
// Per .forge/plans/v2-command-palette.md.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  filterCommands,
  flatten,
  groupCommands,
  type CommandEntry,
  type GroupedCommands,
} from "./registry";

const RECENTS_KEY = "ag-v2.palette.recents";
const RECENTS_CAP = 5;

// Read recents from localStorage, returning entries that still exist in
// `entries` (stale ids are dropped silently).
function readRecents(entries: CommandEntry[]): CommandEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const ids = JSON.parse(raw);
    if (!Array.isArray(ids)) return [];
    const byId = new Map(entries.map((e) => [e.id, e]));
    const out: CommandEntry[] = [];
    for (const id of ids) {
      const e = byId.get(id);
      if (e) out.push({ ...e, group: "recent" });
    }
    return out;
  } catch {
    return [];
  }
}

function writeRecents(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(ids));
  } catch {
    // ignore quota / disabled-storage failures
  }
}

export interface UsePaletteOptions {
  entries: CommandEntry[];
  onNavigate?: (route: string) => void;
}

export interface UsePaletteResult {
  open: boolean;
  setOpen: (next: boolean) => void;
  query: string;
  setQuery: (next: string) => void;
  grouped: GroupedCommands;
  flat: CommandEntry[];
  highlightIndex: number;
  setHighlightIndex: (next: number) => void;
  select: (entry: CommandEntry) => void;
  notice: string | null;
}

// Detects if the global ⌘K / Ctrl+K keydown originated in a text-entry
// surface where the user might be typing literally. We still honor the
// shortcut there — modifier+K is overwhelmingly conventional — but we
// preventDefault to stop browser address-bar focus on Chrome (Linux).
function isPaletteShortcut(e: KeyboardEvent): boolean {
  if (e.key !== "k" && e.key !== "K") return false;
  return e.metaKey || e.ctrlKey;
}

export function usePalette(opts: UsePaletteOptions): UsePaletteResult {
  const { entries, onNavigate } = opts;
  const [open, setOpenState] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(RECENTS_KEY);
      if (!raw) return [];
      const ids = JSON.parse(raw);
      return Array.isArray(ids) ? ids.slice(0, RECENTS_CAP) : [];
    } catch {
      return [];
    }
  });
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the pending auto-close timer set when a forward-promised entry was
  // selected. Called on every "palette closes" path so a stale timer can't
  // fire later and unexpectedly close a freshly-reopened palette.
  const clearNoticeTimer = useCallback(() => {
    if (noticeTimer.current) {
      clearTimeout(noticeTimer.current);
      noticeTimer.current = null;
    }
  }, []);

  // setOpen with side effects: clear query on close, reset highlight on open.
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      if (!next) {
        setQuery("");
        setNotice(null);
        clearNoticeTimer();
      }
      setHighlightIndex(0);
    },
    [clearNoticeTimer],
  );

  // Global keydown listener.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isPaletteShortcut(e)) {
        e.preventDefault();
        setOpenState((prev) => {
          if (prev) {
            setQuery("");
            setNotice(null);
            clearNoticeTimer();
            return false;
          }
          setHighlightIndex(0);
          return true;
        });
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearNoticeTimer]);

  // When the query is empty, prepend any persisted recent commands as a
  // synthetic `recent` group. When the user is searching, recents drop
  // out and the user sees real filtered results.
  const effectiveEntries = useMemo<CommandEntry[]>(() => {
    if (query.trim() !== "") return entries;
    const recents = readRecents(entries).filter((r) =>
      recentIds.includes(r.id),
    );
    if (recents.length === 0) return entries;
    return [...recents, ...entries];
  }, [entries, query, recentIds]);

  const grouped = useMemo<GroupedCommands>(
    () => groupCommands(filterCommands(effectiveEntries, query)),
    [effectiveEntries, query],
  );

  const flat = useMemo(() => flatten(grouped), [grouped]);

  // Keep highlight in bounds when the result set shrinks.
  useEffect(() => {
    if (highlightIndex >= flat.length) {
      setHighlightIndex(Math.max(0, flat.length - 1));
    }
  }, [flat.length, highlightIndex]);

  const select = useCallback(
    (entry: CommandEntry) => {
      // Record to recents (move-to-front, cap 5). Strip the synthetic
      // "recent" group tag when we look up the canonical id.
      const id = entry.id;
      setRecentIds((prev) => {
        const next = [id, ...prev.filter((p) => p !== id)].slice(
          0,
          RECENTS_CAP,
        );
        writeRecents(next);
        return next;
      });

      if (entry.forwardPromised) {
        // Defer-promise: show inline notice for 1200ms, then auto-close.
        const message = `${entry.label} — coming soon`;
        setNotice(message);
        if (noticeTimer.current) clearTimeout(noticeTimer.current);
        noticeTimer.current = setTimeout(() => {
          setOpen(false);
        }, 1200);
        return;
      }

      if (entry.onSelect) {
        entry.onSelect();
      } else if (entry.route && onNavigate) {
        onNavigate(entry.route);
      }
      setOpen(false);
    },
    [onNavigate, setOpen],
  );

  // Clean up the pending notice timer on unmount.
  useEffect(() => {
    return () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    };
  }, []);

  return {
    open,
    setOpen,
    query,
    setQuery,
    grouped,
    flat,
    highlightIndex,
    setHighlightIndex,
    select,
    notice,
  };
}
